import { Headline, SubHeadline, Text } from '~components/Typography';
import { useTranslations } from '~i18n/hooks';
import { CardButton } from '~shared/ui/organisms/CardButton';
import { extractPlaceholders, replacePlaceholders } from '~utils/string';

import { CUSTOM_VALUE_QUERY_PARAM_VALUE } from '../../model/constants';
import {
	isBotWizardFieldName,
	isFieldText,
	isValueMultiSelect,
	isValueSimple,
} from '../../model/guards';
import type {
	BotWizardField,
	BotWizardFieldDataButton,
	BotWizardFieldDataImage,
	BotWizardFieldNames,
	BotWizardPreviewPreset,
	BotWizardPreviewPresetDataItem,
	BotWizardValues,
} from '../../model/types';
import { getDependencyContent } from '../../model/utils';
import { WorkingArea } from '../styles';
import {
	cardButton,
	InfoBlock,
	InfoBlocksWrapper,
	StyledPreviewStep,
	Wrapper,
} from './styles';

export const PreviewStep = ({
	values,
	fields,
	customValues,
	previewPresetStep,
}: {
	values: BotWizardValues;
	fields: Record<BotWizardFieldNames, BotWizardField>;
	previewPresetStep: BotWizardPreviewPreset;
	customValues: { [key in BotWizardFieldNames]?: string };
}) => {
	const t = useTranslations('BotWizard');
	const localData = getDependencyContent({
		data: previewPresetStep.data,
		contentDependency: previewPresetStep.contentDependency,
		values,
	});

	if (!localData) {
		return null;
	}

	const getImage = () => {
		const ethnic = values.ethnic as string;
		const d = getDependencyContent({
			data: fields.ethnic.data,
			contentDependency: fields.ethnic.contentDependency,
			values,
		}) as BotWizardFieldDataImage;
		const el = d.find((e) => e.id === ethnic);

		const image = el?.image;
		const media = el?.media;

		return {
			image: image || '',
			item: media || [],
		};
	};

	const parseText = (item: BotWizardPreviewPresetDataItem) => {
		if (
			item.hidden &&
			(!values[item.hidden] || (values[item.hidden] as string[])?.length === 0)
		) {
			return null;
		}
		const template = item.text;

		const placeholders: BotWizardFieldNames[] = extractPlaceholders(
			template
		).filter((placeholder) => isBotWizardFieldName(placeholder, fields));

		const parsedValues = Object.fromEntries(
			placeholders
				.map((key) => {
					if (isValueSimple(fields[key], values, key)) {
						if (isFieldText(fields[key])) {
							return values[key];
						} else {
							const content = getDependencyContent({
								data: fields[key].data,
								contentDependency: fields[key].contentDependency,
								values,
							}) as BotWizardFieldDataButton | BotWizardFieldDataImage;
							if (values[key] === CUSTOM_VALUE_QUERY_PARAM_VALUE) {
								return customValues[key] ?? '';
							}
							return content.find((el) => el.id === values[key])?.text || '';
						}
					}
					if (isValueMultiSelect(fields[key], values, key)) {
						const content = getDependencyContent({
							data: fields[key].data,
							contentDependency: fields[key].contentDependency,
							values,
						}) as BotWizardFieldDataButton | BotWizardFieldDataImage;
						return (values[key] || [])
							.map((value) => content.find((el) => el.id === value)?.text || '')
							.join(', ');
					}
				})
				.map((el, index) => [placeholders[index], el])
		);

		return replacePlaceholders(template, parsedValues);
	};
	return (
		<WorkingArea>
			<StyledPreviewStep>
				<SubHeadline size="S" capitalizeFirstLetter={true}>
					{t('previewStepTitle', {
						name:
							isValueSimple(fields['name'], values, 'name') && values['name']
								? values['name']
								: '',
					})}
				</SubHeadline>
				<Wrapper>
					{localData
						.filter((el) => el.image)
						.map((item) => ({ ...item, text: parseText(item) }))
						.map((el) => (
							<CardButton
								className={cardButton}
								key={el.text}
								{...getImage()}
								text={el.text || ''}
								width={300}
								height={300}
								quality={90}
								priority={false}
							/>
						))}
					<InfoBlocksWrapper>
						{localData
							.filter((el) => el.title)
							.map((item) => ({ ...item, text: parseText(item) }))
							.filter((item) => item.text)
							.map((el) => (
								<InfoBlock key={el.title}>
									<Headline size="S">{el.title}</Headline>
									<Text capitalizeFirstLetter={true} align="left" size="M">
										{el.text}
									</Text>
								</InfoBlock>
							))}
					</InfoBlocksWrapper>
				</Wrapper>
			</StyledPreviewStep>
		</WorkingArea>
	);
};
